import { useMemo, useCallback, useEffect } from 'react';
import { useProgress } from '@/contexts/ProgressContext';
import type { TicketMeta, TicketStatus, TicketProgress } from '@/types';

interface DerivedTicketState {
  status: TicketStatus;
  acChecked: Record<string, boolean>;
  acDone: number;
  acTotal: number;
  acPct: number;
  acAllDone: boolean;
  quickCheckPassed: boolean;
  /** Lesson 3-step derived workflow per locked decision #2. */
  workflowStep: 'todo' | 'progress' | 'ready' | 'done';
  toggleAC: (acId: string) => void;
  setStatus: (status: TicketStatus) => void;
}

/**
 * Returns derived ticket state + actions. Workflow is *derived* from AC + QC
 * progress (per A.1.4 lesson 3-step variant), not manually settable.
 */
export function useTicketState(ticket: TicketMeta): DerivedTicketState {
  const { getTicket, setTicketStatus, setACChecked } = useProgress();
  const tp: TicketProgress | undefined = getTicket(ticket.id);

  const acChecked = useMemo(() => tp?.acChecked ?? {}, [tp?.acChecked]);
  const acDone = ticket.ac.filter((a) => acChecked[a.id]).length;
  const acTotal = ticket.ac.length;
  const acPct = acTotal === 0 ? 0 : Math.round((acDone / acTotal) * 100);
  const acAllDone = acTotal > 0 && acDone === acTotal;
  const quickCheckPassed = tp?.quickCheck?.state === 'correct';
  // QC *engaged* (any answer · correct OR wrong) per 31 MAY revert — mirrors
  // Ticket.tsx `allDone`. Drives lesson auto-complete below (not quickCheckPassed:
  // a wrong quick-check answer still closes the lesson).
  const qcAnswered = !!tp?.quickCheck?.state;

  const status: TicketStatus = tp?.status ?? 'todo';

  const workflowStep: DerivedTicketState['workflowStep'] = useMemo(() => {
    if (status === 'done') return 'done';
    if (acAllDone && (!ticket.quickCheck || quickCheckPassed)) return 'ready';
    if (acDone > 0 || tp?.quickCheck?.state) return 'progress';
    return 'todo';
  }, [status, acAllDone, quickCheckPassed, acDone, tp?.quickCheck?.state, ticket.quickCheck]);

  const toggleAC = useCallback(
    (acId: string) => {
      setACChecked(ticket.id, acId, !acChecked[acId]);
    },
    [setACChecked, ticket.id, acChecked],
  );

  const setStatus = useCallback(
    (next: TicketStatus) => setTicketStatus(ticket.id, next),
    [setTicketStatus, ticket.id],
  );

  // Auto-complete to 'done' once all AC are checked AND the quick check (if any)
  // is answered (any answer · same gate as Ticket.tsx `allDone`). There is NO
  // reviewer in self-study, so a manual review → done step just parks the ticket
  // at «прохожу — не уходит» forever.
  // Extended 8 JUN 2026 (Genie · smoke): `practice` + `mission` were left off the
  // first pass and stayed on the old manual «Перенести в Сделано» flow while
  // lessons auto-advanced — inconsistent. The student self-checks their AC on
  // these too, so they auto-complete as well. Still genuinely manual (a real
  // submission step): bug / lab / test / exam / homework.
  const autoCompletes =
    ticket.type === 'docs' ||
    ticket.type === 'task' ||
    ticket.type === 'feature' ||
    ticket.type === 'master' ||
    ticket.type === 'mission' ||
    ticket.type === 'practice' ||
    ticket.type === 'ai' ||
    ticket.type === 'design' ||
    ticket.type === 'metrics';
  const lessonReady = acAllDone && (!ticket.quickCheck || qcAnswered);

  // Per Bug 5 spec · auto-progress workflow:
  // todo → in-progress on first AC tick or QC engagement;
  // in-progress → review when all AC checked AND QC (if any) passed;
  // review/done remain manual transitions ("Move to Done" CTA).
  useEffect(() => {
    if (status === 'done') return;
    if (autoCompletes && lessonReady) {
      setStatus('done');
      return;
    }
    if (workflowStep === 'ready' && status !== 'review') {
      setStatus('review');
      return;
    }
    if (
      (workflowStep === 'progress' || workflowStep === 'ready') &&
      status === 'todo'
    ) {
      setStatus('in-progress');
    }
  }, [status, workflowStep, setStatus, autoCompletes, lessonReady]);

  return {
    status,
    acChecked,
    acDone,
    acTotal,
    acPct,
    acAllDone,
    quickCheckPassed,
    workflowStep,
    toggleAC,
    setStatus,
  };
}
